import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';

const REDIRECT_DELAY_MS = 1500;
const INVALID_LINK_DELAY_MS = 750;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('checking');
  const invalidLinkTimerRef = useRef(null);
  const redirectTimerRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    const applySessionStatus = (session) => {
      window.clearTimeout(invalidLinkTimerRef.current);

      if (session?.user) {
        setStatus((currentStatus) => (currentStatus === 'success' ? currentStatus : 'ready'));
        return;
      }

      invalidLinkTimerRef.current = window.setTimeout(() => {
        if (mounted) setStatus('invalid');
      }, INVALID_LINK_DELAY_MS);
    };

    const syncSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;
      applySessionStatus(session);
    };

    syncSession();

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
        applySessionStatus(session);
        return;
      }

      if (event === 'SIGNED_OUT') {
        applySessionStatus(null);
      }
    });

    return () => {
      mounted = false;
      window.clearTimeout(invalidLinkTimerRef.current);
      window.clearTimeout(redirectTimerRef.current);
      listener?.subscription?.unsubscribe();
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!password) {
      setError('Enter a new password.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setError('');
    setStatus('submitting');

    const { error: updateError } = await supabase.auth.updateUser({ password });

    if (updateError) {
      setError(updateError.message || 'Unable to update your password right now.');
      setStatus('ready');
      return;
    }

    setStatus('success');
    redirectTimerRef.current = window.setTimeout(async () => {
      await supabase.auth.signOut();
      navigate('/login', { replace: true });
    }, REDIRECT_DELAY_MS);
  };

  const isBusy = status === 'checking' || status === 'submitting' || status === 'success';

  return (
    <Container maxWidth="xs" sx={{ mt: 8 }}>
      <Box sx={{ boxShadow: 3, p: 4, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h5" gutterBottom>Reset password</Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a new password for your account.
            </Typography>
          </Box>

          {status === 'checking' ? (
            <Alert severity="info">Checking your recovery link...</Alert>
          ) : null}

          {status === 'invalid' ? (
            <Alert severity="warning">
              This recovery link is invalid or has expired. Request a new password reset email.
            </Alert>
          ) : null}

          {status === 'success' ? (
            <Alert severity="success">
              Your password has been updated. Redirecting you to login...
            </Alert>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="New password"
              fullWidth
              margin="normal"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={status === 'checking' || status === 'invalid' || status === 'success'}
            />
            <TextField
              label="Confirm password"
              fullWidth
              margin="normal"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              disabled={status === 'checking' || status === 'invalid' || status === 'success'}
            />
            <Button
              variant="contained"
              type="submit"
              fullWidth
              sx={{ mt: 2 }}
              disabled={isBusy || status === 'invalid'}
            >
              {status === 'submitting' ? 'Updating...' : 'Update password'}
            </Button>
          </Box>

          <Link component={RouterLink} to={status === 'invalid' ? '/forgot-password' : '/login'} underline="hover">
            {status === 'invalid' ? 'Request a new reset link' : 'Back to login'}
          </Link>
        </Stack>
      </Box>
    </Container>
  );
};

export default ResetPasswordPage;
